// Direct canvas manipulation for Studio mode.
//
// Drag horizontally → angle.
// Drag vertically → spread.
// Wheel             → cell size (Shift+wheel = quant).
//
// Only active while Studio mode is on. A small floating tag shows the live
// values during drag so the user understands what they're affecting.

import { findDotgradLayer } from '@/lib/studioMode';
import { updateLayer, beginEdit, endEdit } from '@/core/state';
import { on } from '@/lib/dom';

export function attachCanvasDrag(): () => void {
  const canvas = document.querySelector('.vp-canvas') as HTMLCanvasElement | null;
  if (!canvas) return () => {};

  canvas.classList.add('st-canvas-grab');

  let dragging = false;
  let start: { x: number; y: number; angle: number; spread: number } | null = null;
  const tag = makeTag();

  const onDown = (ev: PointerEvent): void => {
    if (ev.button !== 0) return;
    if (ev.shiftKey || ev.metaKey || ev.ctrlKey) return; // leave to other handlers
    const layer = findDotgradLayer();
    if (!layer) return;
    dragging = true;
    start = {
      x: ev.clientX,
      y: ev.clientY,
      angle: typeof layer.params['angle'] === 'number' ? (layer.params['angle'] as number) : 0,
      spread: typeof layer.params['spread'] === 'number' ? (layer.params['spread'] as number) : 800,
    };
    canvas.setPointerCapture(ev.pointerId);
    beginEdit();
    showTag(ev.clientX, ev.clientY, `${start.angle | 0}°  ·  spread ${start.spread | 0}`);
  };

  const onMove = (ev: PointerEvent): void => {
    if (!dragging || !start) return;
    const dx = ev.clientX - start.x;
    const dy = ev.clientY - start.y;
    const angle = ((start.angle + dx * 0.4) % 360 + 360) % 360;
    const spread = clamp(Math.round(start.spread - dy * 4), 8, 2048);
    const layer = findDotgradLayer();
    if (layer) {
      updateLayer(layer.id, { params: { angle: Math.round(angle), spread } });
    }
    showTag(ev.clientX, ev.clientY, `${Math.round(angle)}°  ·  spread ${spread}`);
  };

  const onUp = (ev: PointerEvent): void => {
    if (!dragging) return;
    dragging = false;
    start = null;
    try { canvas.releasePointerCapture(ev.pointerId); } catch { /* ignored */ }
    endEdit();
    hideTag();
  };

  const onWheel = (ev: WheelEvent): void => {
    if (ev.metaKey || ev.ctrlKey) return; // viewport handles zoom
    const layer = findDotgradLayer();
    if (!layer) return;
    ev.preventDefault();
    const isQuant = ev.shiftKey;
    const key = isQuant ? 'quant' : 'cell';
    const cur = typeof layer.params[key] === 'number' ? (layer.params[key] as number) : (isQuant ? 0 : 8);
    const dir = ev.deltaY < 0 ? 1 : -1;
    const min = isQuant ? 0 : 2;
    const max = isQuant ? 16 : 16;
    const next = clamp(cur + dir, min, max);
    if (next === cur) return;
    beginEdit();
    updateLayer(layer.id, { params: { [key]: next } });
    endEdit();
    showTag(ev.clientX, ev.clientY, `${isQuant ? 'quant' : 'pixel'} ${next}`);
    bumpHide();
  };

  const offDown = on(canvas, 'pointerdown', onDown);
  const offMove = on(canvas, 'pointermove', onMove);
  const offUp = on(canvas, 'pointerup', onUp);
  const offCancel = on(canvas, 'pointercancel', onUp);
  const offWheel = on(canvas, 'wheel', onWheel as (ev: Event) => void, { passive: false });

  return () => {
    offDown();
    offMove();
    offUp();
    offCancel();
    offWheel();
    canvas.classList.remove('st-canvas-grab');
    tag.remove();
  };

  function makeTag(): HTMLElement {
    const t = document.createElement('div');
    t.className = 'st-drag-tag mono';
    document.body.appendChild(t);
    return t;
  }
  function showTag(x: number, y: number, text: string): void {
    tag.textContent = text;
    tag.style.left = `${x + 16}px`;
    tag.style.top = `${y + 16}px`;
    tag.classList.add('show');
  }
  function hideTag(): void {
    tag.classList.remove('show');
  }
  let hideT: ReturnType<typeof setTimeout> | null = null;
  function bumpHide(): void {
    if (hideT !== null) clearTimeout(hideT);
    hideT = setTimeout(() => hideTag(), 700);
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}
